import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "../api/axios";
import './styles/LoginSignup.css';
import { Helmet } from "react-helmet-async";
import NotFoundPage from "./NotFoundPage";

const RegisterInvite = () => {
  const [searchParams] = useSearchParams();
  const invite = searchParams.get("invite");
  const invitedEmail = searchParams.get("email") || "";
  const navigate = useNavigate();

  // User types their email, it's not autofilled
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    username: "",
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(true);
  const [inviteValid, setInviteValid] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Only run verification if there's an invite and invitedEmail in URL
    if (!invite || !invitedEmail) {
      setLoading(false);
      setInviteValid(false);
      setError("Missing invite token or email.");
      return;
    }
    const verify = async () => {
      try {
        const res = await axios.post("/api/auth/verify-invite", {
          invite,
          email: invitedEmail,
        });
        setInviteValid(res.data.valid);
        if (!res.data.valid) setError("Invalid or expired invite link.");
      } catch {
        setError("Invalid or expired invite link.");
        setInviteValid(false);
      } finally {
        setLoading(false);
      }
    };
    verify();
  }, [invite, invitedEmail]);

  const handleChange = e => setFormData(f => ({
    ...f,
    [e.target.name]: e.target.value,
  }));

const handleSubmit = async e => {
  e.preventDefault();
  setError("");
  if (!formData.email && !formData.username) {
    setError("You must enter either an email or a username.");
    return;
  }
  // Only check invitedEmail if user is trying to use an email
  if (formData.email) {
    if (invitedEmail && formData.email !== invitedEmail) {
      setError(`You can only use the email address this invitation was sent to: ${invitedEmail}`);
      return;
    }
  }
  if (formData.password !== formData.confirmPassword) {
    setError("Passwords do not match.");
    return;
  }
  setSubmitting(true);
  try {
    await axios.post("/api/auth/register-invite", {
      invite,
      email: formData.email,
      name: formData.name,
      username: formData.username,
      password: formData.password,
    });
    navigate("/login?registered=1");
  } catch (err) {
    setError(err.response?.data?.error || "Registration failed.");
  }
  setSubmitting(false);
};


  if (!invite || !invitedEmail || (loading === false && !inviteValid)) {
    return <NotFoundPage />;
  }

  if (loading) return <div className="auth-container"><p>Checking invite...</p></div>;
  if (!inviteValid) return <div className="auth-container"><p className="error">{error}</p></div>;

  return (
    <>
      <Helmet>
        <title>Admin - Website Name</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="auth-page-wrapper">
        <div className="auth-container">
          <h2>Complete Your Registration</h2>
          {invitedEmail && (
            <p className="info" style={{ margin: "12px 0" }}>
              You may sign up using a username, an email, or both. <br />
              <b>Note:</b> If you wish to use an email, it must be the address this invitation was sent to: <span style={{ color: "#5A4D3B" }}>{invitedEmail}</span>
            </p>
          )}

          {formData.email && invitedEmail && formData.email !== invitedEmail && (
            <p className="error" style={{marginTop: 8}}>
              You can only use the email address this invitation was sent to: <b>{invitedEmail}</b>
            </p>
          )}
          {error && <p className="error">{error}</p>}
          <form onSubmit={handleSubmit}>
            <input name="name" type="text" placeholder="Name" value={formData.name} onChange={handleChange} required />
            <input
              name="username"
              type="text"
              placeholder="Username (optional)"
              value={formData.username}
              onChange={handleChange}
              autoComplete="username"
            />
            <input
              name="email"
              type="email"
              placeholder={
                invitedEmail
                  ? `Email (optional) — ${invitedEmail}`
                  : "Email (optional)"
              }
              value={formData.email}
              onChange={handleChange}
              autoComplete="email"
            />
            <input name="password" type="password" placeholder="Password" value={formData.password} onChange={handleChange} required />
            <input name="confirmPassword" type="password" placeholder="Confirm Password" value={formData.confirmPassword} onChange={handleChange} required />
            <button type="submit" disabled={submitting}>{submitting ? "Registering..." : "Register"}</button>
          </form>
        </div>
      </div>
    </>
  );
};


export default RegisterInvite;